import { observable, computedFrom, BindingEngine, bindable, bindingMode, autoinject } from 'aurelia-framework';

@autoinject
export class DropdownChecklist {

    constructor(public bindingEngine: BindingEngine, private element: Element) {
        this.selectedItems = [];

        this.selectedItemsPropertySubscription = this.bindingEngine
            .propertyObserver(this, 'selectedItems')
            .subscribe((n, o) => this.selectedItemsPropertyChanged(n, o));

    }

    attached() {
        if (!this.selectedItemsPropertySubscription) {
            this.selectedItemsPropertySubscription = this.bindingEngine
                .propertyObserver(this, 'selectedItems')
                .subscribe((n, o) => this.selectedItemsPropertyChanged(n, o));

            if (this.selectedItems) {
                this.selectedItemsPropertyChanged(this.selectedItems, null);
            }
        }
    }

    detached() {
        if (this.selectedItemsCollectionSubscription) {
            this.selectedItemsCollectionSubscription.dispose();
            delete this.selectedItemsCollectionSubscription;
        }
        if (this.selectedItemsPropertySubscription) {
            this.selectedItemsPropertySubscription.dispose();
            delete this.selectedItemsPropertySubscription;
        }
    }

    @bindable({ defaultBindingMode: bindingMode.oneWay }) readonly: boolean;
    @bindable({ defaultBindingMode: bindingMode.oneWay }) tabIndex: string;
    @bindable({ defaultBindingMode: bindingMode.oneWay }) placeholder: string;  // the placeholder string to use when contorl is used as input.
    @bindable({ defaultBindingMode: bindingMode.oneWay }) caption: string;  //the caption string to use when control is used as filter. e.g. States: All
    @bindable({ defaultBindingMode: bindingMode.oneWay }) captionMember: string;    //the property, if any, to use when building out selected items summary for caption.  If not specified, it will use the displayMember
    @bindable({ defaultBindingMode: bindingMode.twoWay }) selectedItems: Array<any>;
    @bindable({ defaultBindingMode: bindingMode.twoWay }) all: boolean;
    @bindable({ defaultBindingMode: bindingMode.oneWay }) @observable items: Array<any>;
    @bindable({ defaultBindingMode: bindingMode.oneWay }) filterCaption: string;
    @bindable({ defaultBindingMode: bindingMode.oneWay }) valueMember: string;
    @bindable({ defaultBindingMode: bindingMode.oneWay }) displayMember: string;
    @bindable({ defaultBindingMode: bindingMode.oneWay }) groupValueMember: string;
    @bindable({ defaultBindingMode: bindingMode.oneWay }) groupDisplayMember: string;
    @bindable({ defaultBindingMode: bindingMode.oneWay }) filterDisplay: string = 'normal';  //set to 'none' to disable filter
    @bindable({ defaultBindingMode: bindingMode.oneWay }) mode: string = 'filter';    //set to 'input' to cause this control to behave as a normal form input, instead of a filter control.

    filter: string;
    showPlaceHolder: boolean;
    buttonCaption: string;
    filterInput: HTMLInputElement;
    selectedItemsCollectionSubscription;
    selectedItemsPropertySubscription;

    itemsChanged() {
        this.updateCaption();
    }


    @computedFrom("selectedItems.length", "items.length", "filter")
    get filteredItems(): Array<any> {

        let filterString = (this.filter || "").toLowerCase();

        if (!filterString || this.filterDisplay == 'none') {
            return this.items;
        }


        //no groups
        if (!this.groupDisplayMember) {
            return this.items.filter((item) => this.filterItem(item, filterString));
        }
        else { //group filtering logic

            var groups = this.items;

            return groups.map((group) => {
                //  if group name matches, then include all items from that group.
                var groupName = group[this.groupDisplayMember];
                var groupItems: Array<any>;

                if (groupName.toLowerCase().indexOf(filterString) > -1) {
                    groupItems = group[this.groupValueMember];    //include all items, since the group name itself matches
                }
                else {
                    groupItems = group[this.groupValueMember].filter((item) => this.filterItem(item, filterString));
                }

                var filteredGroup = {};
                filteredGroup[this.groupDisplayMember] = groupName;
                filteredGroup[this.groupValueMember] = groupItems;

                return filteredGroup;
            });
        }
    }

    clearSelectedItems() {
        this.selectedItems = [];

        setTimeout(() => {
            //event when selection changed
            let changeEvent = new CustomEvent('change', {
                detail: {
                    value: this.selectedItems
                },
                bubbles: true
            });
            this.element.dispatchEvent(changeEvent);
        }, 0);
    }

    selectAll() {
        if (!this.groupDisplayMember) {
            //no groups
            this.selectedItems = this.filteredItems.map((item) => {
                if (this.valueMember) {
                    return item[this.valueMember];
                } else {
                    return item;
                }
            });
        } else {
            //handle groups
            this.selectedItems = [];
            var groups = this.filteredItems;
            groups.forEach((group) => {
                group[this.groupValueMember].forEach((item) => {
                    if (this.valueMember) {
                        this.selectedItems.push(item[this.valueMember]);
                    } else {
                        this.selectedItems.push(item);
                    }
                });
            });
        }

        //event when selection changed
        setTimeout(() => {
            let changeEvent = new CustomEvent('change', {
                detail: {
                    value: this.selectedItems
                },
                bubbles: true
            });
            this.element.dispatchEvent(changeEvent);
        }, 0);
    }

    private filterItem(item, filterString) {
        let valueToCheck = ((this.displayMember ? item[this.displayMember] : item) || "").toLowerCase();
        return valueToCheck.indexOf(filterString) > -1
    }

    selectionChanged() {
        this.updateCaption();



        //event when selection changed
        let changeEvent = new CustomEvent('change', {
            detail: {
                value: this.selectedItems
            },
            bubbles: true
        });
        this.element.dispatchEvent(changeEvent);

    }

    updateCaption() {
        if (!(this.selectedItems && this.selectedItems.indexOf)) {
            this.selectedItems = [];
        }

        //control is invalid state if these are not an array.
        if (!this.items || !this.selectedItems) return;

        //by default, this formats the button text to work like a filter (showing caption: All when nothing selected)
        //If mode=input then this will always display the actual selected items summary text

        if ((this.mode !== 'input' && this.selectedItems.length == 0) || this.isAllItemsSelected()) {
            this.showPlaceHolder = false;
            if (this.caption) {
                this.buttonCaption = this.caption + ": All";
            } else {
                this.buttonCaption = "ALL";
            }
            this.all = true;
        }
        else {

            var allSelectedItems = this.selectedItems;

            ////if groups enabled, then have to build selected items manually
            //if (this.groupDisplayMember) {
            //    allSelectedItems = [];
            //    this.items.forEach((group) => {
            //        allSelectedItems = allSelectedItems.concat(group[this.groupValueMember].filter((item) => {
            //            return 
            //        }));
            //    });
            //}

            if (this.selectedItems.length) {

                //items are selected

                this.showPlaceHolder = false;


                //build list of display values
                this.buttonCaption = this.selectedItems.map((selectedItem) => {
                    var foundItem;

                    //for groups, look in the items for each group
                    if (this.groupDisplayMember) {

                        this.items.find((group) => {
                            var groupItems = group[this.groupValueMember];
                            return foundItem = groupItems.find((e) =>
                                (this.valueMember ? e[this.valueMember] : e) == selectedItem
                            );
                        });

                    } else {
                        //otherwise, look in base items list
                        foundItem = (<any>this.items).find((e) =>
                            (this.valueMember ? e[this.valueMember] : e) == selectedItem
                        );
                    }

                    if (!foundItem) {
                        return "";
                    }
                    return this.captionMember || this.displayMember ? foundItem[this.captionMember || this.displayMember] : foundItem;
                }).sort().join(", ");

            } else {
                //nothing selected

                //show or hide placeholder
                if (this.mode == "input") {
                    this.showPlaceHolder = true;
                }
            }


            this.all = false;
        }
    }

    isAllItemsSelected(): boolean {
        var selectedItemCount;
        if (!this.groupDisplayMember) {
            //no groups
            selectedItemCount = this.items.length;
        } else {
            //handle groups
            //(have to count the items)
            selectedItemCount = 0;
            var groups = this.items;
            groups.forEach((group) => {
                group[this.groupValueMember].forEach((item) => {
                    selectedItemCount++;
                });
            });
        }

        return selectedItemCount == this.selectedItems.length;
    }

    selectedItemsPropertyChanged(newValue, oldValue) {
        //console.log("property changed");

        //if (!(this.selectedItems && this.selectedItems.length)){
        //    console.log("property to array");
        //    this.selectedItems = [];
        //    return;
        //}

        this.selectedItemsCollectionSubscription && this.selectedItemsCollectionSubscription.dispose();

        if (this.selectedItems) {
            this.selectedItemsCollectionSubscription = this.bindingEngine.collectionObserver(this.selectedItems).subscribe(
                (splices) => this.selectionChanged());
        }

        this.selectionChanged();
    }

    toggle(e) {
        (<any>$(e.srcElement)).parents('.dropdown').find('button.dropdown-toggle').dropdown('toggle');
    }

    dropdownToggled() {
        //clear and focus the filter input whenever dropdown is expanded
        setTimeout(() => {
            if (this.filterInput) {
                this.filterInput.value = "";
                this.filter = "";
                this.filterInput.focus();
            }
        }, 0);
    }
}